import React, { useState, useEffect, useRef } from 'react';
import styles from '../../styles/Dashboard/Chatbot.module.css';
import ReactMarkdown from 'react-markdown';

const API_BASE_URL = import.meta.env.VITE_API_URL;

const ChatbotArea = ({ tenantUserId }) => {
  const [messages, setMessages] = useState([
    { id: 0, text: 'Welcome to MILO GUIDE! How can I help you today?', sender: 'bot' },
  ]);
  const [input, setInput] = useState('');
  const [tenantKnowledge, setTenantKnowledge] = useState([]);
  const conversationRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    if (conversationRef.current) {
      conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
    }
  }, [messages]);

	useEffect(() => {
	  const fetchHistoryAndKnowledge = async () => {
		try {
		  // Fetch chat history
		  const historyResponse = await fetch(`${API_BASE_URL}/messaging/history?userId=${tenantUserId}`);
		  const historyData = await historyResponse.json();

		  if (historyData.history && Array.isArray(historyData.history)) {
			const mappedMessages = historyData.history.map(item => ({
			  id: item.Id,
			  text: item.Message,
			  sender: item.Sender === 'assistant' ? 'bot' : item.Sender
			}));
			setMessages(mappedMessages);
		  }

		  // Fetch existing Tenant Knowledge
		  const knowledgeResponse = await fetch(`${API_BASE_URL}/messaging/tenant-knowledge?userId=${tenantUserId}`);
		  const knowledgeData = await knowledgeResponse.json();

		  if (knowledgeData.notes && Array.isArray(knowledgeData.notes)) {
			setTenantKnowledge(knowledgeData.notes);
		  }

		} catch (error) {
		  console.error('Error fetching initial data:', error);
		}
	  };
    fetchHistoryAndKnowledge();
  }, [tenantUserId]);

  const handleSendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = { id: Date.now(), text: input, sender: 'user' };
    setMessages(prev => [...prev, userMessage]);
    const messageToSend = input;
    setInput('');

    try {
      const response = await fetch(`${API_BASE_URL}/messaging/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: "ChatUser",
          email: "user@example.com",
          message: messageToSend,
		  userId: tenantUserId,
          source: "Chatbot"
        })
      });
      const data = await response.json();

      // Add interviewer (bot) message immediately
      const botMessage = { id: Date.now() + 1, text: data.message, sender: 'bot' };
      setMessages(prev => [...prev, botMessage]);

      // Wait briefly then fetch Reporter Note asynchronously
      setTimeout(async () => {
        const noteResponse = await fetch(`${API_BASE_URL}/messaging/latest-note?userId=${tenantUserId}`);
        const noteData = await noteResponse.json();

        if (noteData.note && noteData.note.trim()) {
          setTenantKnowledge(prev => [...prev, noteData.note]);
        }
      }, 1000); // short delay to let backend process the note

    } catch (error) {
      console.error('Error sending message:', error);
    }

    // Immediately clear input
    setInput('');
  };

  return (
    <div className={styles.chatbotContainer}>
      <div className={styles.chatWindow}>
        <h2 className={styles.chatHeading}>Chatbot</h2>
        <div className={styles.conversation} ref={conversationRef}>
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`${styles.message} ${
                msg.sender === 'user' ? styles.userMessage : styles.botMessage
              }`}
            >
              <div className={styles.markdownContent}>
                <ReactMarkdown>{msg.text}</ReactMarkdown>
              </div>
            </div>
          ))}
        </div>
        <div className={styles.inputArea}>
          <input
            type="text"
            placeholder="Type your message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSendMessage(); }}
          />
          <button onClick={handleSendMessage}>Send</button>
        </div>
      </div>

		<div className={styles.tenantKnowledge}>
		  <h2 className={styles.tenantHeading}>Tenant Knowledge</h2>
		  <div className={styles.knowledgeContent}>
			{tenantKnowledge.length === 0 ? (
			  <p className={styles.knowledgeText}>No tenant preferences recorded yet.</p>
			) : (
			  <ul className={styles.knowledgeList}>
				{tenantKnowledge.map((note, index) => (
				  <li key={index}>{note}</li>
				))}
			  </ul>
			)}
		  </div>
		</div>
    </div>
  );
};

export default ChatbotArea;
